/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Prompt } from 'react-router-dom';
import { isNil, defaultTo, get, parseInt } from 'lodash';

import validation from './validation';
import actions from '../../redux/actions';
import protonesUtils from '../../utils/protones';
import commonUtils from '../../utils/common';
import liferay from '../../services/api';
import InputText from '../../components/FormInputText';
import DatePicker from '../../components/FormDatePicker';
import ReadOnlyText from '../../components/InputText';
import InputSelect from '../../components/FormInputSelect';
import Button from '../../components/Button';
import FieldLock from '../../components/FormFieldLock';
import styles from './styles.module.scss';

import type {
  ReduxFormProps,
  Translate,
  Fetchable,
  Production,
  Territory,
  UsageType,
  SaveUsageParams,
  Channel,
  Broadcasting,
  BroadcastType,
  Platform,
  ProductionType,
} from '../../types';

const mapStateToProps = (state, ownProps) => {
  const { productions } = state.productions;
  const selector = formValueSelector(ownProps.form || 'usage');
  const selectedProductionId = commonUtils.toNumber(selector(state, 'productionId'));
  const selectedProduction = (productions.data || []).find(({ id }) => id === selectedProductionId);
  let selectedProductionTypeId;
  if (selectedProduction && selectedProduction.productionType) {
    selectedProductionTypeId = selectedProduction.productionType.id;
  }
  const locksValues = selector(
    state,
    'productionLocked',
    'episodeLocked',
    'broadcastingLocked',
    'channelLocked',
    'platformLocked',
    'broadcastTypeLocked',
    'usageTypeLocked',
    'startDateLocked',
    'endDateLocked',
    'territoryLocked',
    'exclusiveLocked',
    'exclusiveNotesLocked',
    'notesLocked'
  );
  return {
    isCreate: selector(state, 'id') ? false : true,
    selectedBroadcastingId: commonUtils.toNumber(selector(state, 'broadcastingId')),
    selectedChannelId: commonUtils.toNumber(selector(state, 'channelId')),
    selectedProductionId,
    selectedProductionTypeId,
    selectedBroadcastTypeId: commonUtils.toNumber(selector(state, 'broadcastTypeId')),
    selectedUsageTypeId: commonUtils.toNumber(selector(state, 'usageTypeId')),
    locksValues,
  };
};

type Props = ReduxFormProps & {
  formName?: string,
  title?: string,
  selectedProductionId?: number,
  selectedBroadcastingId?: number,
  selectedChannelId?: number,
  selectedBroadcastTypeId?: number,
  selectedProductionTypeId?: number,
  selectedUsageTypeId?: number,
  locksValues: { [lockedProperty: string]: boolean },
  isLoading: boolean,
  isPreset?: boolean,
  isWorklistUsage?: boolean,
  isWorklistItemUsage?: boolean,
  fetchProductionList: typeof actions.fetchProductionList,
  onCreateProduction?: () => any,
  handleSubmit: (callback: mixed) => any,
  save: (usageParams: SaveUsageParams) => mixed,
  saveAsPreset: (usageParams: SaveUsageParams) => mixed,
  cancel: () => mixed,
  // RR
  router: any,
  t: Translate,
  readOnly?: boolean,
  usageNameVisibile: boolean,
  actionButtonsVisible: boolean,
  isCreate: boolean,
  canLeaveDirtyForm?: boolean,
};

type TabId = '1' | '2' | '3';
type State = {
  productions: Fetchable<Production[]>,
  territories: Fetchable<Territory[]>,
  usageTypes: Fetchable<UsageType[]>,
  channels: Fetchable<Channel[]>,
  broadcastings: Fetchable<Broadcasting[]>,
  broadcastTypes: Fetchable<BroadcastType[]>,
  productionTypes: Fetchable<ProductionType[]>,
  usageTypes: Fetchable<UsageType[]>,
  broadcastTypes: Fetchable<BroadcastType[]>,
  platforms: Fetchable<Platform[]>,
  visibleTabId: TabId,
};

const isDisabled = ({ error, fetching, data }: Fetchable<any[]>): boolean =>
  !isNil(error) || fetching || !data || data.length === 0;

const autoselect = (props, formKey: string) => (items: any[]) => {
  if (!items) {
    return [];
  }
  if (items.length === 0) {
    props.change(formKey, '');
  } else if ((props.isCreate || !props.pristine) && items.length === 1) {
    props.change(formKey, items[0].id);
  }
  return items;
};

const fetchPlatforms = (selectedChannelId, channels) => {
  const selectedChannel = channels && channels.find(({ id }) => id === selectedChannelId);
  const platforms = get(selectedChannel, 'platforms', []);
  return Promise.resolve(platforms);
};

const isChanged = (key, from, to) => from[key] !== to[key];

class UsageForm extends React.Component<Props, State> {
  state: State = {
    productions: { fetching: true },
    productionTypes: { fetching: true },
    territories: { fetching: true },
    usageTypes: { fetching: true },
    channels: { fetching: false },
    broadcastings: { fetching: true },
    broadcastTypes: { fetching: true },
    platforms: { fetching: false },
    visibleTabId: '1',
  };

  componentDidMount() {
    this.props.fetchProductionList();
    const channelId = get(this.props.initialValues, 'channelId');
    const platformId = get(this.props.initialValues, 'platformId');
    const broadcastingId = get(this.props.initialValues, 'broadcastingId');
    const broadcastTypeId = get(this.props.initialValues, 'broadcastTypeId');
    // const usageTypeId = get(this.props.initialValues, 'usageTypeId');
    let productionTypeId;
    const productionId = get(this.props.initialValues, 'productionId');
    liferay.productions.find().then(productions => {
      const selectedProduction = productions.find(({ id }) => id === productionId);
      if (selectedProduction && selectedProduction.productionType) {
        productionTypeId = selectedProduction.productionType.id;
      }
      this._fetchOptions(
        'productionTypes',
        'productionTypeId',
        liferay.productionTypes.find({ broadcastTypeId: null, usageTypeId: null })
      );
      this._fetchOptions(
        'broadcastTypes',
        'broadcastTypeId',
        liferay.broadcastTypes.find({ productionTypeId, usageTypeId: null })
      );
      this._fetchOptions(
        'usageTypes',
        'usageTypeId',
        liferay.usageTypes.find({ broadcastTypeId, productionTypeId })
      );
    });

    let channelsPromise = Promise.resolve();
    if (broadcastingId) {
      channelsPromise = liferay.broadcastings.find().then(broadcastings => {
        const selectedBroadcasting = (broadcastings || []).find(
          broadcasting => broadcasting.id === broadcastingId
        );
        if (!selectedBroadcasting || !selectedBroadcasting.enteId) return;
        channelsPromise = this._fetchOptions(
          'channels',
          'channelId',
          liferay.channels.find(selectedBroadcasting.enteId)
        ).then(channels => {
          autoselect(this.props, 'channelId')(channels);
          this.props.change('channelId', channelId);
          this._fetchOptions('platforms', 'platformId', fetchPlatforms(channelId, channels)).then(
            platforms => {
              autoselect(this.props, 'platformId')(platforms);
              this.props.change('platformId', platformId);
            }
          );
        });
      });
    }
    try {
      Promise.all([
        this._fetchOptions('productions', 'productionId', liferay.productions.find()),
        this._fetchOptions('territories', 'territoryId', liferay.territories.find()),
        this._fetchOptions('broadcastings', 'broadcastingId', liferay.broadcastings.find()).then(
          autoselect(this.props, 'broadcastingId')
        ),
        channelsPromise,
      ]);
    } catch (error) {
      console.warn(error);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      selectedBroadcastingId,
      selectedChannelId,
      selectedBroadcastTypeId,
      selectedProductionTypeId,
      // selectedUsageTypeId,
    } = this.props;
    const { broadcastings } = this.state;
    if (isChanged('selectedBroadcastingId', prevProps, this.props)) {
      // ho cambiato l'emittente
      if (selectedBroadcastingId) {
        // ne ho selezionata una quindi richiedo i valori delle combo dipendenti (solo canali)
        const selectedBroadcasting = (broadcastings.data || []).find(
          broadcasting => broadcasting.id === selectedBroadcastingId
        );
        if (selectedBroadcasting && selectedBroadcasting.enteId) {
          this._fetchOptions(
            'channels',
            'channelId',
            liferay.channels.find(selectedBroadcasting.enteId)
          ).then(channels => {
            // selezione automatica se c'è un unico canale
            autoselect(this.props, 'channelId')(channels);
            this.props.change('platformId', '');
            if (selectedChannelId) {
              this._fetchOptions(
                'platforms',
                'platformId',
                fetchPlatforms(selectedChannelId, channels)
              ).then(autoselect(this.props, 'platformId'));
            }
          });
        }
      } else {
        // ho selezionato nessuna emittente quindi rimuovo i valori delle combo dipendenti
        this.setState({ channels: { fetching: false }, platforms: { fetching: false } }, () => {
          this.props.change('channelId', '');
          this.props.change('platformId', '');
        });
      }
    }
    if (isChanged('selectedChannelId', prevProps, this.props)) {
      const {
        channels: { data: channels },
      } = this.state;
      if (selectedChannelId && channels) {
        this._fetchOptions(
          'platforms',
          'platformId',
          fetchPlatforms(selectedChannelId, channels)
        ).then(autoselect(this.props, 'platformId'));
      } else {
        this.setState({ platforms: { fetching: false } }, () => {
          this.props.change('platformId', '');
        });
      }
    }

    if (isChanged('selectedProductionTypeId', prevProps, this.props)) {
      this._fetchOptions(
        'broadcastTypes',
        'broadcastTypeId',
        liferay.broadcastTypes.find({
          productionTypeId: selectedProductionTypeId,
          usageTypeId: null,
        })
      ).then(autoselect(this.props, 'broadcastTypeId'));
      this._fetchOptions(
        'usageTypes',
        'usageTypeId',
        liferay.usageTypes.find({
          productionTypeId: selectedProductionTypeId,
          broadcastTypeId: selectedBroadcastTypeId,
        })
      ).then(autoselect(this.props, 'usageTypeId'));
    }
    if (isChanged('selectedBroadcastTypeId', prevProps, this.props)) {
      this._fetchOptions(
        'usageTypes',
        'usageTypeId',
        liferay.usageTypes.find({
          productionTypeId: selectedProductionTypeId,
          broadcastTypeId: selectedBroadcastTypeId,
        })
      ).then(autoselect(this.props, 'usageTypeId'));
    }
  }

  _fetchOptions(
    stateKey: string,
    formKey: string,
    fetchItemsPromise: Promise<any[]>
  ): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      this.setState({ [stateKey]: { fetching: true } });
      try {
        const items = await fetchItemsPromise;
        this.setState({ [stateKey]: { data: items, fetching: false } }, () => resolve(items));
      } catch (error) {
        console.warn(error);
        const { name, message } = error;
        this.setState(
          {
            [stateKey]: { fetching: false, error: { name, message } },
          },
          () => reject(error)
        );
      }
    });
  }

  _renderOptions = (fetchable: Fetchable<any>): React$Element<any>[] => {
    const { t } = this.props;
    if (fetchable.error) {
      return [
        <option key={'error'} value={null}>
          {fetchable.error.message || 'Errore'}
        </option>,
      ];
    } else if (fetchable.fetching) {
      return [
        <option key={'fetching'} value={null}>
          {t('LOADING')}
        </option>,
      ];
    } else if (fetchable.data) {
      if (fetchable.data.length === 0) {
        return [
          <option key={'undefined'} value={null}>
            {t('NO_VALUE_AVAILABLE')}
          </option>,
        ];
      } else {
        return [
          <option key={'null'} value={null}>
            {t('SELECT_A_VALUE')}
          </option>,
        ].concat(
          // $FlowFixMe
          fetchable.data.map(({ id, name }, index) => (
            <option key={id || index} value={id || ''}>
              {name}
            </option>
          ))
        );
      }
    } else {
      return [<option key={'undefined'}>{t('NO_VALUE_AVAILABLE')}</option>];
    }
  };

  _handleCancel = () => {
    this.props.reset('usage');
    this.props.cancel && this.props.cancel();
  };

  _handleSave = formValues => {
    const usageParams: SaveUsageParams = protonesUtils.getUsageSaveParamsFromObject(formValues);
    this.props.save && this.props.save(usageParams);
  };

  _handleSaveAsPreset = formValues => {
    const usageParams: SaveUsageParams = protonesUtils.getUsageSaveParamsFromObject(formValues);
    this.props.saveAsPreset && this.props.saveAsPreset(usageParams);
  };

  _handleSelectChange = event => {
    const { onCreateProduction } = this.props;
    if (event.target.value === 'createNewProduction' && typeof onCreateProduction === 'function') {
      event.preventDefault();
      onCreateProduction();
    }
  };

  render() {
    const {
      handleSubmit,
      t,
      isPreset,
      isLoading,
      valid,
      readOnly = false,
      usageNameVisibile,
      actionButtonsVisible,
      dirty,
      canLeaveDirtyForm = false,
    } = this.props;
    return (
      <form className={styles.form} onSubmit={handleSubmit(this._handleSave)}>
        {!canLeaveDirtyForm && (
          <Prompt when={dirty} message={location => t('CONFIRM_EXIT_DIRTY_FORM')} />
        )}
        <div className={styles.formHeader}>
          <Field
            component={'input'}
            type="hidden"
            name={'id'}
            label={'id'}
            disabled={readOnly || isLoading}
          />
          {!!usageNameVisibile && (
            <Field
              component={InputText}
              label={isPreset ? t('USAGE_PRESET_NAME') : t('USAGE_NAME')}
              name={'name'}
              disabled={readOnly || isLoading}
            />
          )}
        </div>
        <div className={styles.tabTitles}>
          {this._renderTabTitle('1', t('USAGE_TAB_1'))}
          {this._renderTabTitle('2', t('USAGE_TAB_2'))}
          {this._renderTabTitle('3', t('USAGE_TAB_3'))}
        </div>
        <div className={styles.tabContent}>{this._renderVisibleTab()}</div>
        {actionButtonsVisible && (
          <div className={styles.formButtons}>
            <Button
              text={t('RESET')}
              onClick={this._handleCancel}
              // disabled={readOnly || isLoading || pristine}
              disabled={readOnly || isLoading}
              loading={isLoading}
            />
            {!isPreset && (
              <Button
                text={t('SAVE_AS_PRESET')}
                onClick={handleSubmit(this._handleSaveAsPreset)}
                // disabled={isLoading || !valid || pristine}
                disabled={isLoading || !valid}
                loading={isLoading}
              />
            )}
            <Button
              text={t('SAVE')}
              onClick={handleSubmit(this._handleSave)}
              // disabled={readOnly || isLoading || !valid || pristine}
              disabled={readOnly || isLoading || !valid}
              loading={isLoading}
              appearance={'primary'}
            />
          </div>
        )}
      </form>
    );
  }

  _renderTabTitle = (tabId: TabId, label: string) => {
    const isActive = this.state.visibleTabId === tabId;
    return (
      <div
        className={isActive ? styles.tabTitleActive : styles.tabTitle}
        onClick={() => this.setState({ visibleTabId: tabId })}
      >
        {label}
      </div>
    );
  };

  _renderVisibleTab = () => {
    const {
      visibleTabId,
      channels,
      broadcastings,
      broadcastTypes,
      platforms,
      productionTypes,
    } = this.state;
    const {
      selectedProductionId,
      isPreset,
      isWorklistUsage,
      isWorklistItemUsage,
      readOnly = false,
      isLoading,
      initialValues,
      locksValues: {
        productionLocked = false,
        episodeLocked = false,
        broadcastingLocked = false,
        channelLocked = false,
        platformLocked = false,
        broadcastTypeLocked = false,
        usageTypeLocked = false,
        startDateLocked = false,
        endDateLocked = false,
        territoryLocked = false,
        exclusiveLocked = false,
        exclusiveNotesLocked = false,
        notesLocked = false,
      },
      t,
    } = this.props;
    const { productions, territories, usageTypes } = this.state;
    const selectedProduction =
      productions.data &&
      productions.data.find(
        production => selectedProductionId && production.id === selectedProductionId
      );
    const availableProductionTypes = productionTypes.data || [];
    switch (visibleTabId) {
      case '1':
        return (
          <div className={styles.fieldSet}>
            <div>
              <Field
                component={InputSelect}
                name={'productionId'}
                title={t('PRODUCTION')}
                label={<div className={styles.labelWithButton}>{t('PRODUCTION')}</div>}
                disabled={
                  !isNil(productions.error) ||
                  productions.fetching ||
                  readOnly ||
                  isLoading ||
                  (((initialValues.productionLocked && !isWorklistUsage) || productionLocked) &&
                    !isPreset)
                }
                onChange={this._handleSelectChange}
              >
                {this._renderOptions({
                  ...productions,
                  data: (productions.data || []).filter(production => {
                    const { productionType } = production;
                    return (
                      productionType != null &&
                      availableProductionTypes.map(({ id }) => id).includes(productionType.id)
                    );
                  }),
                }).concat(
                  isWorklistUsage || isWorklistItemUsage
                    ? [
                        <option key={'createNewProduction'} value={'createNewProduction'}>
                          {`+ ${t('CREATE_NEW_PRODUCTION')}`}
                        </option>,
                      ]
                    : []
                )}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div className={styles.stackableRow}>
              <ReadOnlyText
                label={t('PRODUCTION_TYPE')}
                value={defaultTo(get(selectedProduction, 'productionType.name'), '')}
                disabled
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div className={styles.stackableRow}>
              <ReadOnlyText
                label={t('AREA')}
                value={defaultTo(get(selectedProduction, 'area.name'), '')}
                disabled
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div className={styles.stackableRow}>
              <ReadOnlyText
                label={t('PRODUCER_NAME')}
                value={defaultTo(get(selectedProduction, 'producer.name'), '')}
                disabled
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div className={styles.stackableRow}>
              <ReadOnlyText
                label={t('EDITION')}
                value={defaultTo(get(selectedProduction, 'edition'), '')}
                disabled
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div className={styles.stackableRow}>
              <ReadOnlyText
                label={t('USAGE_QUANTITY')}
                value={defaultTo(get(selectedProduction, 'quantity'), '')}
                disabled
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'productionLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputText}
                label={t('EPISODE')}
                name={'episode'}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.episodeLocked && !isWorklistUsage) || episodeLocked) &&
                    !isPreset)
                }
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'episodeLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                name={'broadcastingId'}
                parse={parseInt}
                label={t('BROADCASTER')}
                disabled={
                  readOnly ||
                  isLoading ||
                  isDisabled(broadcastings) ||
                  (((initialValues.broadcastingLocked && !isWorklistUsage) || broadcastingLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(broadcastings)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'broadcastingLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                label={t('CHANNEL')}
                name={'channelId'}
                parse={parseInt}
                disabled={
                  readOnly ||
                  isLoading ||
                  isDisabled(channels) ||
                  (((initialValues.channelLocked && !isWorklistUsage) || channelLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(channels)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'channelLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                label={t('PLATFORM_CHANNEL')}
                name={'platformId'}
                parse={parseInt}
                disabled={
                  readOnly ||
                  isLoading ||
                  isDisabled(platforms) ||
                  (((initialValues.platformLocked && !isWorklistUsage) || platformLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(platforms)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'platformLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                label={t('BROADCAST_TYPE')}
                name={'broadcastTypeId'}
                parse={parseInt}
                disabled={
                  readOnly ||
                  isLoading ||
                  isDisabled(broadcastTypes) ||
                  (((initialValues.broadcastTypeLocked && !isWorklistUsage) ||
                    broadcastTypeLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(broadcastTypes)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'broadcastTypeLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                name={'usageTypeId'}
                label={t('USAGE_TYPE')}
                parse={parseInt}
                disabled={
                  isDisabled(usageTypes) ||
                  readOnly ||
                  isLoading ||
                  (((initialValues.usageTypeLocked && !isWorklistUsage) || usageTypeLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(usageTypes)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'usageTypeLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
          </div>
        );
      case '2':
        return (
          <div className={styles.fieldSet}>
            <div>
              <Field
                component={DatePicker}
                label={t('START_LICENSE_DATE')}
                name={'startDate'}
                parse={protonesUtils.parseDateMillis}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.startDateLocked && !isWorklistUsage) || startDateLocked) &&
                    !isPreset)
                }
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'startDateLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={DatePicker}
                label={t('END_LICENSE_DATE')}
                name={'endDate'}
                parse={protonesUtils.parseDateMillis}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.endDateLocked && !isWorklistUsage) || endDateLocked) &&
                    !isPreset)
                }
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'endDateLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                name={'territoryId'}
                label={t('TERRITORY')}
                disabled={
                  isDisabled(territories) ||
                  readOnly ||
                  isLoading ||
                  (((initialValues.territoryLocked && !isWorklistUsage) || territoryLocked) &&
                    !isPreset)
                }
              >
                {this._renderOptions(territories)}
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'territoryLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputSelect}
                name={'exclusive'}
                label={t('EXCLUSIVE')}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.exclusiveLocked && !isWorklistUsage) || exclusiveLocked) &&
                    !isPreset)
                }
              >
                <option value={true}>{t('YES')}</option>
                <option value={false}>{t('NO')}</option>
              </Field>
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'exclusiveLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
            <div>
              <Field
                component={InputText}
                name={'exclusiveNotes'}
                label={t('EXCLUSIVE_NOTES')}
                multiline
                rows={5}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.exclusiveNotesLocked && !isWorklistUsage) ||
                    exclusiveNotesLocked) &&
                    !isPreset)
                }
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'exclusiveNotesLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
          </div>
        );
      case '3':
        return (
          <div className={styles.fieldSet}>
            <div>
              <Field
                component={InputText}
                name={'notes'}
                label={t('NOTES')}
                multiline
                rows={5}
                disabled={
                  readOnly ||
                  isLoading ||
                  (((initialValues.notesLocked && !isWorklistUsage) || notesLocked) && !isPreset)
                }
              />
              {(isPreset || isWorklistUsage) && (
                <Field
                  type="checkbox"
                  component={FieldLock}
                  name={'notesLocked'}
                  disabled={readOnly || isLoading}
                />
              )}
            </div>
          </div>
        );
      default:
        break;
    }
  };
}

export default withTranslation()(
  connect(
    mapStateToProps,
    actions
  )(
    reduxForm({
      validate: validation,
      enableReinitialize: true,
      //$FlowFixMe
      // destroyOnUnmount: !(keys.IS_ENV_DEVELOPMENT && module.hot),
      destroyOnUnmount: true,
    })(UsageForm)
  )
);
